import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { FilePdfOutlined, LoadingOutlined, WarningOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { ReactComponent as UploadIcon } from "../../../assets/svg/Upload.svg";
import { ReactComponent as AllCyclesIcon } from "../../../assets/svg/all-cycles.svg";
import { Select, DatePicker, Button, Upload, Form, Input, Modal, Tooltip } from "antd";
import {
  CONSENT_VIA_SELECT_OPTIONS,
  CONSENT_ERROR_CODES,
  CONSENT_ACTION_TYPES,
  INDEFINITE_EXPIRATION_MONTHS
} from "../../../constant/consent";
import { ReactComponent as CircleCloseIcon } from "../../../assets/svg/circle-close.svg";
import {
  WHITE,
  GRAY_200,
  GRAY_400,
  GRAY_700,
  GRAY_600,
  PRIMARY_50,
  PRIMARY_600,
  PRIMARY_700,
  ERROR_500,
  ERROR_600,
  ERROR_700
} from "../../../constant/colors";
import { fontWeights, sizes } from "../../../constant/styles";
import { DATE_FORMAT, dateFormatter } from "../../../utils/date";
import moment from "moment-timezone";
import { fileTypesAll, MAX_FILE_SIZE, MB } from "../../Uploader";
import { useGetSetting } from "../../../hooks/getSetting";
import ErrorMessage from "../../customComponent/customMessages/ErrorMessage";
import useStandardizedTracking, {
  EventNames,
  ContentTypes,
  FeatureGroups
} from "../../../utils/pendo/useStandardizedTracking";

const CircleCloseIconStyled = styled(CircleCloseIcon)`
  path {
    stroke: ${ERROR_700};
  }
}`;

const GrayAllCyclesIcon = styled(AllCyclesIcon)`
  width: 12px;
  height: 12px;
  path {
    stroke: ${GRAY_700};
  }
`;

// Constants
const DETAILS_MAX_LENGTH = 75;

// Use same file types as PatientAttachment (from Uploader.js)
const ACCEPTED_FILE_TYPES = fileTypesAll;

// Form field names (used in Ant Design Form)
const FORM_FIELD_NAMES = {
  CONSENT_VIA: "consentVia",
  DATE: "date",
  DETAILS: "details"
};

// API field names (sent to backend)
const API_FIELD_NAMES = {
  EFFECTIVE_DATE: "effectiveDate",
  VIA: "via",
  NOTE: "note",
  FILE: "file"
};

// Styled components
const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
`;

const FormRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: stretch;
`;

const FieldContainer = styled.div`
  flex: ${(props) => props.flex || 1};
  display: flex;
  flex-direction: column;
  gap: 3px;

  /* Override Ant Design Form.Item styles to match our design */
  .ant-form-item {
    margin-bottom: 0;
  }

  /* Style validation error messages */
  .ant-form-item-explain-error {
    font-size: ${sizes.small};
    margin-top: 3px;
    color: ${ERROR_500};
  }
`;

const FieldLabel = styled.div`
  font-weight: ${fontWeights.semibold};
  font-size: ${sizes.small};
  line-height: 1.5em;
`;

const StyledSelect = styled(Select)`
  .ant-select-selector {
    border-radius: 10px !important;
  }

  /* Ensure validation states work properly */
  &.ant-select-status-error .ant-select-selector {
    border-color: ${ERROR_500} !important;
    box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2) !important;
  }
`;

const StyledDatePicker = styled(DatePicker)`
  border-radius: 10px !important;

  /* Ensure validation states work properly */
  &.ant-picker-status-error {
    border-color: ${ERROR_500} !important;
    box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2) !important;
  }
`;

const StyledTextArea = styled(Input.TextArea)`
  border-radius: 10px !important;

  /* Ensure the textarea itself has rounded corners */
  .ant-input {
    border-radius: 10px !important;
  }
`;

const AttachmentsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const AttachmentsHeader = styled.div`
  font-weight: ${fontWeights.semibold};
  font-size: ${sizes.small};
  line-height: 1.5em;
`;

const AttachmentsRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  min-height: 32px;
`;

const SizeLimitText = styled.span`
  font-weight: ${fontWeights.regular};
  font-size: ${sizes.small};
  color: ${GRAY_600};
  line-height: 1.5;
`;

const UploadButton = styled(Button)`
  background-color: ${PRIMARY_600};
  border-color: ${PRIMARY_600};
  border-radius: 8px;
  font-weight: ${fontWeights.semibold};
  font-size: ${sizes.small};
  height: auto;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: ${PRIMARY_700};
    border-color: ${PRIMARY_700};
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const AttachmentItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 5px;
  background-color: ${GRAY_200};
  border-radius: 5px;
  height: 24px;
  border: 1px solid ${(props) => (props.hasError ? ERROR_600 : "transparent")};
  flex: 0 1 auto;
  min-width: 80px;
  max-width: 100%;
`;

const AttachmentIcon = styled.div`
  width: 15px;
  height: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const AttachmentText = styled.span`
  font-weight: ${fontWeights.regular};
  font-size: ${sizes.small};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.5;
  flex: 1;
  min-width: 0;
`;

const ActionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 4px 5px;
  border-radius: 5px;
  cursor: pointer;
  height: 24px;
  flex-shrink: 0;

  &:hover {
    background-color: ${PRIMARY_50};
  }
`;

const ActionIcon = styled.div`
  width: 15px;
  height: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ActionText = styled.span`
  font-weight: ${fontWeights.regular};
  font-size: ${sizes.small};
  white-space: nowrap;
`;

const AlertContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const AlertIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const AlertText = styled.span`
  font-weight: ${fontWeights.semibold};
  font-size: ${sizes.small};
  color: ${ERROR_600};
  line-height: 1.5;
  white-space: nowrap;
`;

const DetailsSection = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: flex-end;
  align-self: stretch;
  gap: 10px;
`;

const TextAreaContainer = styled(FieldContainer)`
  flex: 1;
`;

const RightSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  height: 52px;
`;

const CharacterCount = styled.span`
  font-weight: ${fontWeights.regular};
  font-size: ${sizes.small};
  color: ${GRAY_600};
  width: 100%;
  text-align: left;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 20px;
`;

const CancelButton = styled.button`
  padding: 4px 0px;
  background: transparent;
  border: none;
  border-bottom: 1.5px solid ${PRIMARY_600};
  font-weight: ${fontWeights.semibold};
  font-size: ${sizes.medium};
  color: ${PRIMARY_600};
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const SaveButton = styled.button`
  padding: 5px 10px;
  background-color: ${PRIMARY_600};
  border: none;
  border-radius: 10px;
  font-weight: ${fontWeights.semibold};
  font-size: ${sizes.medium};
  color: ${WHITE};
  cursor: pointer;

  &:hover {
    background-color: ${PRIMARY_700};
  }

  &:disabled {
    background-color: ${GRAY_400};
    cursor: not-allowed;
  }
`;

/**
 * Pendo keys and copy per action. Replaces the old `isRevokeAction` boolean,
 * which could only ever express two of the three actions — adding "Patient
 * declined" (MVP-29825) made a third branch unavoidable. The boolean also
 * compared `actionType` against CONSENT_STATUS_TYPES rather than
 * CONSENT_ACTION_TYPES and only worked because the string values happen to
 * coincide; keying this map off CONSENT_ACTION_TYPES fixes that too.
 *
 * `submitErrorMessage` is deliberately NOT run through t(): it is only ever sent
 * as a Pendo tracking dimension, never rendered to the user, so it must stay a
 * stable English key. Localising it would silently split the analytics series.
 */
const ACTION_CONFIG = {
  [CONSENT_ACTION_TYPES.RECEIVED]: {
    formContext: "record_consent_form",
    saveContentKey: "save_consent_button",
    saveButtonId: "btn_save_consent",
    submittedContentKey: "record_consent_form_submitted",
    submitErrorMessage: "There was an error registering consent.",
    detailsPlaceholder: "consent.forms.details_placeholder_collect"
  },
  [CONSENT_ACTION_TYPES.REVOKED]: {
    formContext: "revoke_consent_form",
    saveContentKey: "save_revoke_button",
    saveButtonId: "btn_save_revoke",
    submittedContentKey: "revoke_consent_form_submitted",
    submitErrorMessage: "There was an error revoking consent.",
    detailsPlaceholder: "consent.forms.details_placeholder_revoke"
  },
  [CONSENT_ACTION_TYPES.DECLINED]: {
    formContext: "decline_consent_form",
    saveContentKey: "save_decline_button",
    saveButtonId: "btn_save_decline",
    submittedContentKey: "decline_consent_form_submitted",
    submitErrorMessage: "There was an error recording the patient decline.",
    detailsPlaceholder: "consent.forms.details_placeholder_decline"
  }
};

const GeneralConsentForm = ({ onCancel, onSave, onSubmit, actionType, onError }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [modal, contextHolder] = Modal.useModal();
  const { track } = useStandardizedTracking();

  // Watch the details field to trigger re-renders when it changes
  const detailsValue = Form.useWatch(FORM_FIELD_NAMES.DETAILS, form);

  const [isLoading, setIsLoading] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);
  const [fileError, setFileError] = useState(null);

  const [consentExpirationMonths] = useGetSetting(["consent.expiration_months"]);

  const getActionTypeLabel = () => t(`consent.forms.action_types.${actionType}`).toLowerCase();
  const actionConfig = ACTION_CONFIG[actionType];

  // Step 4: Track box impression when the form is displayed (component mounts)
  useEffect(() => {
    if (actionType) {
      track(EventNames.BOX_IMPRESSION, {
        pageKey: "patient_overview",
        featureGroup: FeatureGroups.ECONSENT,
        contentType: ContentTypes.CONSENT,
        contentKey: actionConfig.formContext,
        layout: "panel",
        additionalInfo: {
          panel_title: t("consent.forms.register_title"),
          has_received_via_field: true,
          has_date_field: true,
          has_upload_file_option: true,
          has_details_field: true,
          received_via_options: CONSENT_VIA_SELECT_OPTIONS.map((option) => option.value)
        }
      });
    }
  }, [actionType, actionConfig, t, track]);

  const shouldDisableDate = (current) => {
    if (!current) return false;

    // Disable future dates
    if (current.isAfter(moment(), "day")) {
      return true;
    }

    // When expiration is indefinite, any past date is allowed; otherwise disable dates older than the expiration window
    if (
      consentExpirationMonths !== INDEFINITE_EXPIRATION_MONTHS &&
      current.isBefore(moment().subtract(consentExpirationMonths, "months"), "day")
    ) {
      return true;
    }

    return false;
  };

  const handleFileUpload = (file) => {
    // Step 5: Track upload file button click
    track(EventNames.BUTTON_CLICK, {
      pageKey: "patient_overview",
      featureGroup: FeatureGroups.ECONSENT,
      contentType: ContentTypes.CONSENT,
      contentKey: "upload_file_button",
      additionalInfo: {
        button_text: t("consent.forms.upload_file"),
        button_id: "btn_upload_consent_file",
        form_context: actionConfig.formContext,
        is_optional: true
      }
    });

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      const errorMessage = t("patientApplications.attachments.exceeds_file_size", { maxSize: MAX_FILE_SIZE / MB });
      ErrorMessage(errorMessage, { width: 500, textAlign: "left" });
      setAttachedFile(file);
      setFileError(t("consent.forms.upload_failed"));
      return false; // Prevent auto upload but show the file with error state
    }

    setAttachedFile(file);
    setFileError(null); // Clear any previous errors
    return false; // Prevent auto upload
  };

  const handleCancel = () => {
    // Check if form has been touched or file has been uploaded
    const formHasChanges = form.isFieldsTouched() || attachedFile !== null;

    if (formHasChanges) {
      modal.confirm({
        centered: true,
        icon: <ExclamationCircleOutlined style={{ color: "#F79009" }} />,
        title: t("consent.unsaved_changes.title"),
        content: t("consent.unsaved_changes.message"),
        okText: t("consent.unsaved_changes.discard_button"),
        cancelText: t("consent.unsaved_changes.stay_button"),
        onOk: () => {
          onCancel();
        }
      });
    } else {
      onCancel();
    }
  };

  const handleSave = async () => {
    // Declare variables outside try block so they're accessible in catch
    let values = null;
    let detailsText = "";

    try {
      // Prevent submission if there's a file error
      if (fileError) {
        ErrorMessage(t("consent.forms.fix_file_error"));
        return;
      }

      // Validate form using Ant Design validation
      values = await form.validateFields();

      // Step 6: Track Save button click before submission
      detailsText = values[FORM_FIELD_NAMES.DETAILS] || "";
      track(EventNames.BUTTON_CLICK, {
        pageKey: "patient_overview",
        featureGroup: FeatureGroups.ECONSENT,
        contentType: ContentTypes.CONSENT,
        contentKey: actionConfig.saveContentKey,
        additionalInfo: {
          button_text: t("consent.forms.save_button"),
          button_id: actionConfig.saveButtonId,
          form_context: actionConfig.formContext,
          received_via: values[FORM_FIELD_NAMES.CONSENT_VIA],
          consent_date: values[FORM_FIELD_NAMES.DATE].format("YYYY-MM-DD"),
          has_file_uploaded: !!(attachedFile && !fileError),
          has_details: !!detailsText,
          details_length: detailsText.length
        }
      });

      setIsLoading(true);

      if (onError) {
        onError(null);
      }

      // Implemented: File upload and note handling for consent actions

      const formData = new FormData();
      formData.append(API_FIELD_NAMES.EFFECTIVE_DATE, values[FORM_FIELD_NAMES.DATE].toDate().toISOString());
      formData.append(API_FIELD_NAMES.VIA, values[FORM_FIELD_NAMES.CONSENT_VIA]);
      formData.append(API_FIELD_NAMES.NOTE, values[FORM_FIELD_NAMES.DETAILS] || "");

      if (attachedFile && !fileError) {
        formData.append(API_FIELD_NAMES.FILE, attachedFile);
      }

      if (onSubmit) {
        await onSubmit(formData);
      }

      // Step 7: Track form submission success
      track(EventNames.FORM_SUBMITTED, {
        pageKey: "patient_overview",
        featureGroup: FeatureGroups.ECONSENT,
        contentType: "form",
        contentKey: actionConfig.submittedContentKey,
        productSource: "backend", // Backend confirms consent registration/revocation
        additionalInfo: {
          form_id: actionConfig.formContext,
          submission_status: "success",
          consent_type: "general_consent",
          received_via: values[FORM_FIELD_NAMES.CONSENT_VIA],
          consent_date: values[FORM_FIELD_NAMES.DATE].format("YYYY-MM-DD"),
          file_uploaded: !!(attachedFile && !fileError),
          file_name: attachedFile?.name ? "[REDACTED_FILENAME]" : null,
          details_provided: !!detailsText,
          registration_method: "manual"
        }
      });

      if (onSave) {
        onSave({
          consentVia: values[FORM_FIELD_NAMES.CONSENT_VIA],
          date: values[FORM_FIELD_NAMES.DATE] ? dateFormatter(values[FORM_FIELD_NAMES.DATE]) : null,
          details: values[FORM_FIELD_NAMES.DETAILS] || "",
          attachedFile
        });
      }
    } catch (error) {
      // If validation fails (e.g., required fields are empty), it throws an error with an errorFields property
      if (error.errorFields) {
        return;
      }

      // Extract error data from Sails badRequest response structure
      // Structure: { errors: [{ type: "error", header: "Error", code: "...", message: "..." }] }
      const errorData = error?.response?.data?.errors?.[0];
      const errorCode = errorData?.code;
      const apiErrorMessage = errorData?.message || error?.message || "";

      // Check if it's a file upload error by error code (more reliable than status check)
      const isFileUploadError = errorCode === CONSENT_ERROR_CODES.FILE_UPLOAD_ERROR;

      // Step 7: Track form submission error
      track(EventNames.FORM_SUBMITTED, {
        pageKey: "patient_overview",
        featureGroup: FeatureGroups.ECONSENT,
        contentType: "form",
        contentKey: actionConfig.submittedContentKey,
        productSource: "backend", // Backend confirms error
        additionalInfo: {
          form_id: actionConfig.formContext,
          submission_status: "error",
          error_message: actionConfig.submitErrorMessage,
          consent_type: "general_consent",
          ...(values && {
            received_via: values[FORM_FIELD_NAMES.CONSENT_VIA],
            consent_date: values[FORM_FIELD_NAMES.DATE]?.format("YYYY-MM-DD")
          }),
          file_uploaded: !!(attachedFile && !fileError),
          file_name: attachedFile?.name ? "[REDACTED_FILENAME]" : null,
          registration_method: "manual",
          details_provided: !!detailsText
        }
      });

      // If it's a file upload error (400), show it inline next to the file
      if (isFileUploadError) {
        setFileError(t("consent.forms.upload_failed"));
        ErrorMessage(apiErrorMessage);
      } else {
        // For other errors, use the generic error handler
        const errorMessage = t(`consent.forms.error_messages.${actionType}`);
        if (onError) {
          onError(errorMessage);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormContainer>
      {contextHolder}
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          [FORM_FIELD_NAMES.CONSENT_VIA]: undefined,
          [FORM_FIELD_NAMES.DATE]: moment(),
          [FORM_FIELD_NAMES.DETAILS]: ""
        }}
      >
        <FormRow>
          <FieldContainer>
            <FieldLabel>{t("consent.forms.consent_via_label", { actionType: getActionTypeLabel() })} *</FieldLabel>
            <Form.Item
              name={FORM_FIELD_NAMES.CONSENT_VIA}
              rules={[
                {
                  required: true,
                  message: t("consent.forms.field_required", {
                    fieldName: t("consent.forms.consent_via_label", { actionType: getActionTypeLabel() })
                  })
                }
              ]}
            >
              <StyledSelect
                placeholder={t("consent.forms.consent_via_placeholder")}
                style={{ width: "100%" }}
                options={CONSENT_VIA_SELECT_OPTIONS.map((option) => ({
                  value: option.value,
                  label: t(option.label)
                }))}
                allowClear
              />
            </Form.Item>
          </FieldContainer>
          <FieldContainer>
            <FieldLabel>{t("consent.forms.date_label")} *</FieldLabel>
            <Form.Item
              name={FORM_FIELD_NAMES.DATE}
              rules={[
                {
                  required: true,
                  message: t("consent.forms.field_required", {
                    fieldName: t("consent.forms.date_label")
                  })
                }
              ]}
            >
              <StyledDatePicker
                format={DATE_FORMAT}
                placeholder={DATE_FORMAT}
                style={{ width: "100%" }}
                disabledDate={shouldDisableDate}
              />
            </Form.Item>
          </FieldContainer>
        </FormRow>
      </Form>
      <AttachmentsSection>
        <AttachmentsHeader>{t("consent.forms.attachments_title")}</AttachmentsHeader>
        <AttachmentsRow>
          {!attachedFile ? (
            <>
              <Upload beforeUpload={handleFileUpload} showUploadList={false} accept={ACCEPTED_FILE_TYPES}>
                <UploadButton type="primary">
                  {t("consent.forms.upload_file")}
                  <UploadIcon />
                </UploadButton>
              </Upload>
              <SizeLimitText>{t("consent.forms.size_limit", { maxSize: MAX_FILE_SIZE / MB })}</SizeLimitText>
            </>
          ) : (
            <>
              <AttachmentItem hasError={!!fileError}>
                <AttachmentIcon>
                  <FilePdfOutlined style={{ fontSize: sizes.small, color: GRAY_600 }} />
                </AttachmentIcon>
                <Tooltip title={attachedFile?.name}>
                  <AttachmentText id="consent-general-consent-attachment-text">{attachedFile?.name}</AttachmentText>
                </Tooltip>
              </AttachmentItem>

              {fileError && (
                <AlertContainer>
                  <AlertIcon>
                    <WarningOutlined style={{ fontSize: "16px", color: ERROR_600 }} />
                  </AlertIcon>
                  <AlertText>{fileError}</AlertText>
                </AlertContainer>
              )}

              <Upload beforeUpload={handleFileUpload} showUploadList={false} accept={ACCEPTED_FILE_TYPES}>
                <ActionItem>
                  <ActionIcon>
                    <GrayAllCyclesIcon />
                  </ActionIcon>
                  <ActionText>{t("consent.forms.action_replace")}</ActionText>
                </ActionItem>
              </Upload>
              <ActionItem
                onClick={() => {
                  setAttachedFile(null);
                  setFileError(null);
                }}
              >
                <ActionIcon>
                  <CircleCloseIconStyled />
                </ActionIcon>
                <ActionText>{t("consent.forms.action_remove")}</ActionText>
              </ActionItem>
            </>
          )}
        </AttachmentsRow>
      </AttachmentsSection>
      <Form form={form}>
        <DetailsSection>
          <TextAreaContainer>
            <FieldLabel>{t("consent.forms.details_label")}</FieldLabel>
            <Form.Item name={FORM_FIELD_NAMES.DETAILS}>
              <StyledTextArea
                id="consent-general-consent-details"
                placeholder={t(actionConfig.detailsPlaceholder)}
                maxLength={DETAILS_MAX_LENGTH}
                rows={2}
                style={{ resize: "none" }}
              />
            </Form.Item>
          </TextAreaContainer>
          <RightSection>
            <CharacterCount>
              {detailsValue?.length || 0}/{DETAILS_MAX_LENGTH}
            </CharacterCount>
            <ButtonRow>
              {/* type="button" is load-bearing: these sit inside antd's <Form>, and a
                  styled.button defaults to type="submit" — the native submit then makes
                  rc-field-form run its own validateFields(), which races handleSave's and
                  makes handleSave's reject as out-of-date. The sibling RequestConsentForm
                  avoids this by using antd's <Button> (htmlType="button" by default). */}
              <CancelButton type="button" onClick={handleCancel} disabled={isLoading}>
                {t("consent.forms.cancel_button")}
              </CancelButton>
              <SaveButton type="button" onClick={handleSave} disabled={isLoading}>
                {isLoading ? (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <LoadingOutlined style={{ marginRight: "8px", fontSize: "24px" }} />
                    {t("consent.forms.loading_states.saving")}
                  </div>
                ) : (
                  t("consent.forms.save_button")
                )}
              </SaveButton>
            </ButtonRow>
          </RightSection>
        </DetailsSection>
      </Form>
    </FormContainer>
  );
};

// Export only the generic form
export default GeneralConsentForm;
